const siUnits = ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
const binaryUnits = ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];

const siThreshold = 1000;
const binaryThreshold = 1024;

export function fileSizeFormatter(
  bytes: number | undefined,
  decimalPlaces = 1,
  /**
   * @param si - Whether to use SI (decimal) units (true = base 1000) or binary (false = base 1024).
   *             SI units: KB, MB, GB... | Binary units: KiB, MiB, GiB...
   */
  si = true
) {
  const thresh = si ? siThreshold : binaryThreshold;
  const units = si ? siUnits : binaryUnits;

  if (typeof bytes !== 'number') {
    bytes = 0;
  }

  if (Math.abs(bytes) < thresh) {
    return `${Math.round(Math.abs(bytes))} B`;
  }

  let unitIndex = -1;
  const roundingFactor = 10 ** decimalPlaces;

  do {
    bytes /= thresh;
    ++unitIndex;
  } while (
    Math.round(Math.abs(bytes) * roundingFactor) / roundingFactor >= thresh &&
    unitIndex < units.length - 1
  );

  return `${bytes.toFixed(decimalPlaces)} ${units[unitIndex]}`;
}

export function getMaxFittingUnit(bytesList: number[], si = true): string {
  const thresh = si ? siThreshold : binaryThreshold;
  const units = si ? siUnits : binaryUnits;

  let maxUnitIndex = -1;

  for (let bytes of bytesList) {
    if (typeof bytes !== 'number' || bytes < thresh) {
      continue;
    }

    let unitIndex = -1;
    while (bytes >= thresh && unitIndex < units.length - 1) {
      bytes /= thresh;
      unitIndex++;
    }

    if (unitIndex > maxUnitIndex) {
      maxUnitIndex = unitIndex;
    }
  }

  return maxUnitIndex === -1 ? units[0] : units[maxUnitIndex];
}

export const convertToBytes = (size: number, unit: string) => {
  const siIndex = siUnits.indexOf(unit);
  const binaryIndex = siIndex === -1 ? binaryUnits.indexOf(unit) : -1;

  const { thresh, index } =
    siIndex !== -1
      ? { thresh: siThreshold, index: siIndex }
      : binaryIndex !== -1
        ? { thresh: binaryThreshold, index: binaryIndex }
        : {};

  if (index === undefined || thresh === undefined) {
    throw new Error(`Invalid unit: ${unit}`);
  }

  return size * thresh ** (index + 1);
};

export const convertFromBytes = (size: number, unit: string) => {
  const siIndex = siUnits.indexOf(unit);
  const binaryIndex = siIndex === -1 ? binaryUnits.indexOf(unit) : -1;

  const { thresh, index } =
    siIndex !== -1
      ? { thresh: siThreshold, index: siIndex }
      : binaryIndex !== -1
        ? { thresh: binaryThreshold, index: binaryIndex }
        : {};

  if (index === undefined || thresh === undefined) {
    throw new Error(`Invalid unit: ${unit}`);
  }

  for (let i = 0; i <= index; i++) {
    size /= thresh;
  }

  return size;
};
